import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Typography,
} from '@material-ui/core'
import { CoreButton } from 'src/components/core'
import { Icon } from '@iconify/react'
import TextFieldPopup from 'src/components/textfield-popup'
import SuggestionSkeleton from 'src/components/suggestion-skeleton'
import { withStyles } from '@material-ui/core/styles'
import { isMobile } from 'react-device-detect'
import styles from '../styles'

const SectionHeader = ({
  classes,
  section,
  sectionIndex,
  sectionTitleRefs,
  sectionMenuAnchor,
  onSectionTitleChange,
  onSectionMenuOpen,
  onSectionMenuClose,
  onSectionAction,
  onSectionTitleSuggestions,
  sectionTitleSuggestions,
  dragHandleProps,
  draggableProps,
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const hasRequestedSuggestions = useRef(false)

  const handleTitleFocus = useCallback(() => {
    // Show suggestions when focusing on empty section title or default placeholder
    if (
      !section.section_title ||
      section.section_title.trim() === '' ||
      section.section_title === 'New Section'
    ) {
      // Prevent multiple API calls
      if (hasRequestedSuggestions.current) {
        return
      }

      setShowSuggestions(true)
      setIsLoading(true)
      hasRequestedSuggestions.current = true

      onSectionTitleSuggestions(sectionIndex)
    }
  }, [section.section_title, sectionIndex, onSectionTitleSuggestions])

  const handleSuggestionClick = suggestion => {
    onSectionTitleChange(suggestion, sectionIndex)
    setShowSuggestions(false)
    hasRequestedSuggestions.current = false
  }

  const handleRegenerate = () => {
    setIsLoading(true)
    onSectionTitleSuggestions(sectionIndex)
  }

  // Update loading state when suggestions are received
  useEffect(() => {
    if (sectionTitleSuggestions.length > 0) {
      setIsLoading(false)
    }
  }, [sectionTitleSuggestions])

  // Reset the flag when popup is closed
  const handlePopupClose = useCallback(() => {
    setShowSuggestions(false)
    hasRequestedSuggestions.current = false
  }, [])

  return (
    <>
      <Box className={classes.sectionHeader}>
        <span {...dragHandleProps} className={classes.dragHandleRotated}>
          <Icon
            icon="icon-park:drag"
            style={{
              fontSize: isMobile ? '16px' : '20px',
              color: '#838D98 !important',
              marginRight: '8px',
            }}
          />
        </span>

        <div
          ref={el => {
            if (!sectionTitleRefs.current) sectionTitleRefs.current = []
            sectionTitleRefs.current[sectionIndex] = el
          }}
          contentEditable
          suppressContentEditableWarning
          data-placeholder="Section Title"
          className={classes.sectionTitle}
          onBlur={() => {
            const title = sectionTitleRefs.current[sectionIndex]?.textContent
            if (section.section_title !== title) {
              onSectionTitleChange(title, sectionIndex)
            }
          }}
          onFocus={handleTitleFocus}
          style={{ cursor: 'text' }}
        >
          {section.section_title}
        </div>

        <Box className={classes.sectionHeaderActions}>
          <Icon
            icon={'eva:more-vertical-fill'}
            style={{ fontSize: '16px', color: '#838D98' }}
            onClick={e => onSectionMenuOpen(e, sectionIndex)}
          />
        </Box>
      </Box>

      {/* Section Title Suggestions Popup */}
      <TextFieldPopup
        anchorEl={sectionTitleRefs.current?.[sectionIndex]}
        open={showSuggestions}
        onClose={handlePopupClose}
        placement="bottom-start"
        offset={4}
        maxHeight={250}
      >
        <div className={classes.popupContent}>
          <div className={classes.popupTitleContainer}>
            <Typography
              fontSize={14}
              fontWeight={400}
              className={classes.popupTitle}
            >
              Suggested section titles:
            </Typography>
            {sectionTitleSuggestions.length > 0 && (
              <CoreButton
                size="small"
                variant="text"
                color="secondary"
                startIcon={
                  isLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Icon icon="hugeicons:exchange-01" fontSize={16} />
                  )
                }
                onClick={handleRegenerate}
                disabled={isLoading}
              >
                {isLoading ? 'Generating...' : 'Regenerate'}
              </CoreButton>
            )}
          </div>

          <List dense className={classes.suggestionList}>
            {isLoading ? (
              <SuggestionSkeleton />
            ) : sectionTitleSuggestions.length > 0 ? (
              sectionTitleSuggestions.map((suggestion, suggestionIndex) => (
                <ListItem
                  key={suggestionIndex}
                  button
                  className={classes.suggestionItem}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <div className={classes.suggestionItemIcon}>
                    <img
                      src="/assets/AI-Assistant-icon.svg"
                      width={isMobile ? 16 : 24}
                      height={isMobile ? 16 : 24}
                      alt="AI Assistant"
                    />
                  </div>
                  <ListItemText
                    primary={suggestion}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: 400,
                      className: classes.suggestionText,
                    }}
                  />
                </ListItem>
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Typography variant="body2" style={{ color: '#637381' }}>
                  {sectionTitleSuggestions.length === 0
                    ? 'No suggestions available. Try regenerating.'
                    : 'Enter a course topic to get suggestions.'}
                </Typography>
              </div>
            )}
          </List>
        </div>
      </TextFieldPopup>

      <Menu
        anchorEl={sectionMenuAnchor}
        open={Boolean(sectionMenuAnchor)}
        onClose={onSectionMenuClose}
        className={classes.sectionMenu}
      >
        <MenuItem
          onClick={() => onSectionAction('duplicate')}
          className={classes.sectionMenuItem}
        >
          Duplicate
        </MenuItem>
        <MenuItem
          onClick={() => onSectionAction('delete')}
          className={classes.sectionMenuItem}
        >
          Delete
        </MenuItem>
      </Menu>
    </>
  )
}

export default withStyles(styles)(SectionHeader)
